//© all rights reserved to 6over6 Ltd. 2022. This software (the "Software") is
the property of 6over6 Ltd. and its licensors. Any use of the Software is
strictly prohibited unless an explicit license or permission is granted by
6over6 Ltd.
<template>
  <section class="go_wrapper">
    <div class="go_content">
      <div class="go_instruction">{{ store.state.page.qr_instruction }}</div>
      <div class="go_start_app_container">
        <div class="go_qr_wrapper">
          <QR
            v-if="qrInfo"
            class="go_qr"
            :qrInfo="qrInfo"
            :qrWidth="qrWidth"
            :startAnimation="startAnimation"
          ></QR>
        </div>
        <div class="go_svg_container">
          <svg height="100%">
            <svg viewBox="0 0 50 220">
              <line
                text-anchor="middle"
                x1="50%"
                y1="0"
                x2="50%"
                :y2="(qrWidth - 60) / 2 - 20"
                style="stroke: #444444; stroke-width: 1"
              />
            </svg>
            <text text-anchor="middle" x="50%" y="55%">
              {{ store.state.page.qr_instruction_middle }}
            </text>
            <svg viewBox="0 0 50 220">
              <line
                text-anchor="middle"
                x1="50%"
                :y1="(qrWidth - 60) / 2 + 20"
                x2="50%"
                y2="220"
                style="stroke: #444444; stroke-width: 1"
              />
            </svg>
          </svg>
        </div>
        <div class="go_send_sms_container">
          <label class="go_label">{{
            store.state.page.send_sms_assets.title
          }}</label>
          <div class="go_input_container">
            <div class="go_error_msg">{{ error }}</div>
            <input
              placeholder="E.g. +XX 555 5555 5555"
              v-model="phoneNumber"
              @focus="setPrefix"
              @blur="unsetPrefix"
            />
          </div>
          <div class="go_btn_container">
            <div
              :class="[
                'go_btn',
                phoneNumber.length ? 'active' : null,
                disableSmsButton ? 'disabled' : null,
              ]"
              :style="sendLinkButtonStyle"
              @click="sendSms"
            >
              {{ store.state.page.send_sms_assets.link_label }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="go_terms_privacy">
      {{ store.state.page.send_sms_assets.terms_acceptance }}<br />
      <a :href="termsLink" target="_blank" :style="getLinkStyle">{{
        store.state.page.send_sms_assets.terms_of_use
      }}</a>
      {{ store.state.page.send_sms_assets.terms_and_statement }}
      <a
        href="https://luna.io/trust/privacy"
        target="_blank"
        :style="getLinkStyle"
        >{{ store.state.page.send_sms_assets.privacy_policy }}</a
      >
    </div>
    <div class="go_app_store">
      <div class="go_app_icon">
        <img :src="appIconSrc" />
      </div>
      <div class="go_app_store_container">
        <div class="go_app_store_logos">
          <a
            v-if="shouldDisplayAppStore"
            :href="branchIOSDesktop"
            target="_blank"
          >
            <img
              src="https://assets.glasseson.com/web/images/app_store_icon.png"
            />
          </a>
          <a
            v-if="shouldDisplayGoogleStore"
            :href="branchAndroidDesktop"
            target="_blank"
          >
            <img
              src="https://assets.glasseson.com/web/images/play_store_icon.png"
            />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { useStore } from "vuex";
import { defineProps, ref, computed, inject } from "vue";
import QR from "@/components/sections/qrScanPages/QrDisplay.vue";
import AnalyticsEvent from "@/../../../common/models/AnalyticsEvent.js";

const props = defineProps({
  startAnimation: { type: Boolean, default: false },
  branchUrl: { type: String, default: null },
  shouldDisplayAppStore: { type: Boolean, default: true },
  shouldDisplayGoogleStore: { type: Boolean, default: true },
  termsLink: { type: String, default: "https://luna.io/trust/terms" },
  appIconSrc: { type: String, default: "" },
  storeLinks: { type: Object, default: () => {} },
});
const store = useStore();
const api = inject("api");

const qrWidth = 280;
const phoneNumber = ref("");
const error = ref(null);
const disableSmsButton = ref(false);

const qrInfo = computed(function () {
  return props.branchUrl;
});

const branchIOSDesktop = computed(function () {
  return props.branchUrl + "?$desktop_url=" + props.storeLinks.app_store_link;
});

const branchAndroidDesktop = computed(function () {
  return props.branchUrl + "?$desktop_url=" + props.storeLinks.play_store_link;
});
// weight positive for lighter, negative for darker
function lightenDarkenColor(color, weight) {
  let usePound = false;

  if (color[0] == "#") {
    color = color.slice(1);
    usePound = true;
  }

  let num = parseInt(color, 16);

  let r = (num >> 16) + weight;
  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let g = (num & 0x0000ff) + weight;
  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  let b = ((num >> 8) & 0x00ff) + weight;
  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
}

const sendLinkButtonStyle = computed(function () {
  return {
    "--background": lightenDarkenColor(store.state.Style.themeColor, 40),
    "--background-active": store.state.Style.themeColor,
  };
});

const getLinkStyle = computed(function () {
  return {
    color: store.state.Style.themeColor,
    "font-weight": 700,
  };
});

async function sendSms() {
  if (
    phoneNumber.value.length === 0 ||
    phoneNumber.value === "+" ||
    disableSmsButton.value
  ) {
    return;
  }
  disableSmsButton.value = true;
  api.analytics.log(new AnalyticsEvent("act web get sms", "act", "good"));
  let res = await window.glasseson_wc.sendSms(phoneNumber.value, {
    link: props.branchUrl,
  });
  error.value = res.errorType;
  disableSmsButton.value = false;
}

function setPrefix(e) {
  e.preventDefault();
  phoneNumber.value = "+";
}

function unsetPrefix(e) {
  e.preventDefault();
  if (phoneNumber.value.length > 1) {
    return;
  }
  phoneNumber.value = "";
}
</script>

<style lang="scss" scoped>
.go_wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  a {
    cursor: pointer;
  }
  .go_content {
    display: flex;
    flex-direction: column;
    align-items: center;
    .go_instruction {
      font-weight: 300;
      font-size: 30px;
      line-height: 38px;
      letter-spacing: 0px;
      text-align: center;
      margin-top: 40px;
      margin-bottom: 41px;

      @include desktop_small {
        font-size: 30px;
        line-height: 38px;
        margin-top: 27px;
        margin-bottom: 54px;
      }

      @include tablet {
        font-size: 25px;
        line-height: 31px;
        margin-top: 26px;
        margin-bottom: 24px;
      }
    }
    .go_start_app_container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-around;
      width: 850px;

      @include tablet {
        width: 700px;
      }

      .go_qr_wrapper {
        display: flex;
        flex-direction: column;

        @include tablet {
          width: 215px;
        }

        @include desktop_small {
          width: 130px;
        }
      }
      .go_svg_container {
        height: 220px;
        margin-left: 32px;

        @include tablet {
          height: 178px;
        }

        svg {
          width: 88px;

          text {
            font-size: 31px;
            font-weight: 300;
            line-height: 38px;

            @include tablet {
              font-size: 25px;
              line-height: 31px;
            }
          }
        }
      }
      .go_send_sms_container {
        margin-left: 72px;
        height: 220px;
        justify-content: space-between;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 413px;

        @include tablet {
          height: 180px;
          width: 340px;
          margin-left: 76px;
        }

        .go_label {
          font-weight: 500;
          font-size: 30px;
          line-height: 38px;
          width: 442px;

          @include tablet {
            font-size: 25px;
            line-height: 31px;
          }
        }

        .go_input_container {
          margin: 10px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          input {
            width: 405px;
            height: 65px;
            border: 1px solid #b7b4b4;
            border-radius: 8px;
            padding: 0 10px;
            font-weight: 400;
            font-size: 24px;
            line-height: 97px;

            @include tablet {
              width: 333px;
              height: 53.12px;
              font-size: 20px;
              line-height: 80px;
            }
          }

          .go_error_msg {
            font-size: 14px;
            color: #e95353;
            text-align: left;
            width: 100%;
            margin-left: 5px;
            margin-bottom: 2px;
          }
        }

        .go_btn_container {
          .go_btn {
            background: var(--background);
            border-radius: 20.5px;
            color: #fff;
            font-weight: 400;
            font-size: 30px;
            line-height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 120px;
            cursor: pointer;
            width: 414px;
            height: 68px;

            @include tablet {
              height: 56px;
              width: 340px;
              border-radius: 16px;
            }

            &.disabled {
              opacity: 0.5;
            }

            &.active {
              background: var(--background-active);
              &:active:not(.disabled) {
                opacity: 0.8;
              }
            }
          }
        }
      }
    }
  }

  .go_terms_privacy {
    width: 899px;
    margin-top: 45px;
    font-size: 20px;
    line-height: 31px;

    @include tablet {
      width: 754px;
      margin-top: 38px;
      font-size: 17px;
      line-height: 25px;
    }
  }

  .go_app_store {
    display: flex;
    margin-top: 50px;
    justify-content: center;
    height: 65px;

    @include tablet {
      height: 54px;
    }

    .go_app_icon {
      width: 65px;
      height: 64px;
      margin-right: 60px;

      @include tablet {
        width: 54px;
      }
      img {
        width: 100%;
        height: 100%;
        border-radius: 6px;
      }
    }
    .go_app_store_container {
      font-size: 21px;
      font-weight: bold;
      display: flex;
      flex-direction: column;
      height: 100%;

      .go_app_store_logos {
        display: flex;
        align-items: flex-end;
        height: 100%;
        a {
          height: 100%;
          margin-right: 60px;

          &:last-child {
            margin-right: 0px;
          }

          img {
            height: 100%;
          }
        }
      }
    }
  }
}
</style>
